import React, { useEffect, useState } from 'react';
import golfPlayer from '../../assets/images/golf-player.svg';
import manIcon from '../../assets/images/man.svg';
import womanIcon from '../../assets/images/woman.svg';
import { Player } from '../../types/types';

interface ProgressBarsProps {
  items: Player[];
  activeItems: Player[];
  multipleSelect?: boolean;
  label: string;
  activeGender?: string;
  onItemClick: (players: Player) => void;
}

import {
  ChevronUpDownIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { birthDate2age } from '../../utils/utils';
import NotFind from './notfind';

const SelectBoxPlayer: React.FC<ProgressBarsProps> = ({
  items,
  activeItems,
  onItemClick,
  label,
  activeGender = 'MW',
  multipleSelect = false,
}) => {
  const [modalState, setModalState] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const modalRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setModalState(false);
      }
    };

    if (modalState) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [modalState]);

  const isPlayerDisabled = (playerGender: string) => {
    return (
      (activeGender === 'M' && playerGender === 'W') ||
      (activeGender === 'W' && playerGender === 'M')
    );
  };

  const filteredPlayers = items
    .filter((player) => {
      const search = searchQuery.toLowerCase();
      return (
        `${player.name} ${player.lastName}`.toLowerCase().includes(search) ||
        player.memberNumber?.toString().toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      const aDisabled = isPlayerDisabled(a.gender);
      const bDisabled = isPlayerDisabled(b.gender);
      return aDisabled === bDisabled ? 0 : aDisabled ? 1 : -1;
    });

  return (
    <div
      className={`border relative bg-white cursor-pointer hover:border-gray-400 hover:shadow-md rounded-md shadow-sm ${modalState && 'border-gray-500 hover:border-gray-500'} ${multipleSelect && 'w-[220px] sm:w-[290px]'} ${!multipleSelect && activeItems.length === 1 && 'border-green-600 hover:border-green-700'} ${!multipleSelect && 'w-[190px] sm:w-[280px]'}`}
    >
      <div
        onClick={() => setModalState(true)}
        className="flex p-1.5 justify-between items-center gap-2"
      >
        <img width="22" src={golfPlayer} alt="" />
        <div
          className={`text-xs font-medium ${!multipleSelect && activeItems.length === 0 && 'text-gray-400'}`}
        >
          {!multipleSelect && activeItems.length ? (
            <div className="flex gap-1">
              <b>
                {activeItems[0].title}
                {activeItems[0].lastName}
              </b>

              {activeItems[0].name}

              <small className="text-green-700 ml-1 rounded-full">
                (
                {activeItems[0].hcp
                  ? activeItems[0].hcp.toString().replace('.', ',')
                  : '?'}
                )
              </small>
            </div>
          ) : (
            label
          )}
        </div>
        {multipleSelect && (
          <div className="text-xs text-gray-400 hidden sm:inline-block">
            ({activeItems.length}/{items.length})
          </div>
        )}
        <ChevronUpDownIcon className="w-4" />
      </div>

      {modalState && (
        <div
          ref={modalRef}
          className="animate__animated animate__fadeIn animate__faster absolute overflow-hidden z-40 w-[260px] sm:w-[280px] bg-white border border-gray-400 top-10 right-0 rounded-md shadow-md"
        >
          <div className="border-b shadow-md border-gray-400 gap-2 p-2 flex rounded-t-md">
            <MagnifyingGlassIcon className="w-5 hidden md:inline-block" />
            <input
              className="p-1 flex-1 bg-transparent border-transparent focus:border-transparent focus:ring-0 focus:outline-none"
              placeholder="Spiel suchen ..."
              name="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div
              onClick={() => setModalState(false)}
              className="w-7 h-7 bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center rounded-md"
            >
              <XMarkIcon className="w-5" />
            </div>
          </div>

          <div className="overflow-scroll h-[300px]">
            {filteredPlayers.length === 0 && (
              <div className="text-center flex items-center justify-center h-[280px]">
                <NotFind
                  displayIcon
                  title="Kein Spieler gefunden"
                  description="Die Spieler-Liste ist leer."
                />
              </div>
            )}
            {filteredPlayers.map((player, index) => (
              <div
                key={index}
                onClick={() => {
                  if (!isPlayerDisabled(player.gender)) {
                    onItemClick(player);
                    if (!multipleSelect) setModalState(false);
                  }
                }}
                className={`border-b px-4 group justify-between flex gap-2 items-center p-1 ${activeItems.some(item => String(item.memberNumber) === String(player.memberNumber)) && 'bg-green-50 border-l-green-500 border-l-2'} ${isPlayerDisabled(player.gender) && 'bg-gray-100 text-neutral-500 cursor-not-allowed'}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-7 md:group-hover:w-9 ease-in-out transition-all flex items-center justify-center rounded-full h-7 ${player.gender === 'M' ? 'bg-blue-100' : 'bg-pink-100'}`}
                  >
                    <img
                      className="w-3"
                      src={player.gender === 'M' ? manIcon : womanIcon}
                      alt=""
                    />
                  </div>
                  <div>
                    <div className="flex gap-0.5">
                      <span className="font-bold">
                        {player.title} {player.lastName},
                      </span>
                      {player.name}
                    </div>
                    <div className={`text-xs `}>
                      <span
                        className={`${birthDate2age(player.birthDate) <= 18 ? 'font-medium text-orange-600' : 'text-gray-500'}`}
                      >
                        {player.birthDate
                          ? birthDate2age(player.birthDate)
                          : '?'}{' '}
                        Jahre
                      </span>
                      {` `} / {player.memberNumber}
                    </div>
                  </div>
                </div>
                {activeItems.some(item => String(item.memberNumber) === String(player.memberNumber)) && <CheckIcon className="w-4" />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectBoxPlayer;
